/* eslint-disable no-nested-ternary */
import React, { useState } from "react";

import {
  Button,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Switch,
} from "antd";
import moment from "moment";
import PropTypes from "prop-types";
import RefernEarnAddUsersModal from "./RefernEarnAddUsersModal";

import { createRefernEarnProductMigration } from "../Pages/api";

const RefernEarnAddProductMultipleUsersModal = ({
  visible,
  onClose,
  onSave,
}) => {
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState({});
  const [enableValidity, setEnableValidity] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isAddUsersModalVisible, setIsAddUsersModalVisible] = useState(false);

  // Calculate Total Price
  const calculateTotalPrice = (onetime, sub) => {
    const total = parseFloat(onetime || 0) + parseFloat(sub || 0);
    return Math.round(total * 100) / 100;
  };

  const handleCreateEditRefernEarnProducts = async (values) => {
    setLoading(true);
    try {
      const res = await createRefernEarnProductMigration(values);

      if (res?.code === 201 || res?.code === 200 || res?.code === 202) {
        message.success("Product migration started");
        form.resetFields();
        onSave();
        return { isSaved: true };
      }

      message.error(res?.message || "Something went wrong");
      return { isSaved: false };
    } catch (err) {
      console.log(err);
      if (err?.message) {
        message.error(err.message);
      } else {
        message.error("Something went wrong");
      }

      return { isSaved: false };
    } finally {
      setLoading(false);
    }
  };

  const handleAddUsers = async (users) => {
    const validatedValues = {
      ...formValues,
      userArr: users,
    };

    if (validatedValues.onetimeFee) {
      validatedValues.onetimeFee = parseFloat(validatedValues.onetimeFee);
    }

    if (validatedValues.subscriptionFee) {
      validatedValues.subscriptionFee = parseFloat(
        validatedValues.subscriptionFee
      );
    }

    if (!enableValidity) {
      validatedValues.expireAt = null;
    }

    const { isSaved } = await handleCreateEditRefernEarnProducts(
      validatedValues
    );
    if (isSaved) {
      setIsAddUsersModalVisible(false);
      form.resetFields();
      onClose();
    }

    return { isSaved };
  };

  return (
    <>
      <Modal
        title="Add Product"
        visible={visible}
        onCancel={() => {
          form.resetFields();
          setFormValues({});
          onClose();
        }}
        destroyOnClose
        footer={[
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => {
              form.validateFields().then(() => {
                setIsAddUsersModalVisible(true);
              });
            }}
            style={{ width: "100%" }}
          >
            Add users
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(_, values) => {
            const totalProductPrice = calculateTotalPrice(
              values.onetimeFee || 0,
              values.subscriptionFee || 0
            );

            form.setFieldValue("totalProductPrice", totalProductPrice);
            setFormValues((prevState) => ({
              ...prevState,
              ...values,
              totalProductPrice,
            }));
          }}
        >
          <Form.Item
            name="title"
            label="Product title"
            rules={[{ required: true, message: "Please enter product title" }]}
          >
            <Input placeholder="e.g. Black Friday Drop" />
          </Form.Item>

          <Form.Item name="isExclusive" valuePropName="checked">
            <Switch
              checked={formValues?.isExclusive}
              onChange={(checked) => {
                form.setFieldsValue({
                  isExclusive: checked,
                });

                setFormValues((prevState) => ({
                  ...prevState,
                  isExclusive: checked,
                }));
              }}
            />{" "}
            Mark as Exclusive
          </Form.Item>

          <Row style={{ gap: "16px" }}>
            <Form.Item
              style={{ flex: 1 }}
              name="platformSetup"
              label="Product Setup"
              rules={[
                {
                  required:
                    !formValues?.isExclusive ||
                    formValues?.tier?.length > 0 ||
                    formValues?.platformSetup?.length > 0,
                  message: "Please select product setup",
                },
              ]}
            >
              <Select
                onChange={() => {
                  form.setFieldsValue({
                    tier: "tier1",
                  });

                  setFormValues((prevState) => ({
                    ...prevState,
                    tier: "tier1",
                  }));
                }}
              >
                <Select.Option value="tagmango">TagMango</Select.Option>
                <Select.Option value="freedom">Freedom</Select.Option>
                <Select.Option value="enterprise">Enterprise</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              style={{ flex: 1 }}
              name="tier"
              label="Plan"
              rules={[
                {
                  required:
                    !formValues?.isExclusive ||
                    formValues?.platformSetup?.length > 0 ||
                    formValues?.tier?.length > 0,
                  message: "Please select plan",
                },
              ]}
            >
              <Select
                onChange={(value) => {
                  if (value === "tier1") {
                    // calculate total product price by removing subscription fee
                    const totalProductPrice = calculateTotalPrice(
                      formValues.onetimeFee || 0,
                      0
                    );

                    // remove subscription fee
                    form.setFieldsValue({
                      subscriptionFee: "",
                      totalProductPrice,
                    });

                    setFormValues((prevState) => ({
                      ...prevState,
                      subscriptionFee: 0,
                      totalProductPrice,
                    }));
                  }
                }}
              >
                <Select.Option value="tier1">Basic</Select.Option>
                {formValues?.platformSetup ===
                "tagmango" ? null : formValues?.platformSetup === "freedom" ? (
                  <>
                    <Select.Option value="tier2">Pro</Select.Option>
                    <Select.Option value="tier3">Advance</Select.Option>
                  </>
                ) : (
                  <>
                    <Select.Option value="tier2">Pro</Select.Option>
                    <Select.Option value="tier3">Advance</Select.Option>
                    <Select.Option value="tier4">Ultimate</Select.Option>
                  </>
                )}
              </Select>
            </Form.Item>
          </Row>

          {["tagmango"].includes(formValues?.platformSetup) ? null : (
            <Form.Item
              name="onetimeFee"
              label="One-time fee"
              rules={[{ required: true, message: "Please enter one-time fee" }]}
            >
              <Input prefix="Rs." type="number" placeholder="25000" />
            </Form.Item>
          )}

          {["tier2", "tier3", "tier4"].includes(formValues?.tier) ? (
            <Row style={{ gap: "16px" }}>
              <Form.Item
                style={{ flex: 1 }}
                name="subscriptionFee"
                label="Subscription fee"
                rules={[
                  { required: true, message: "Please enter subscription fee" },
                ]}
              >
                <Input prefix="Rs." type="number" placeholder="25000" />
              </Form.Item>
              <Form.Item
                style={{ flex: 1 }}
                name="paymentPlan"
                label="Payment plan"
                rules={[
                  { required: true, message: "Please select payment plan" },
                ]}
              >
                <Select>
                  <Select.Option value="monthly">Monthly</Select.Option>
                  <Select.Option value="quarterly">Quarterly</Select.Option>
                  <Select.Option value="halfyearly">Half-Yearly</Select.Option>
                  <Select.Option value="yearly">Yearly</Select.Option>
                </Select>
              </Form.Item>
            </Row>
          ) : null}

          {["tagmango"].includes(formValues?.platformSetup) ? null : (
            <Form.Item label="Total product price" name="totalProductPrice">
              <Input disabled />
            </Form.Item>
          )}

          <Form.Item
            name="productLink"
            label="Product purchase link (Only one-time mangoes eligible)"
            rules={[{ required: true, message: "Please enter purchase link" }]}
          >
            <Input placeholder="Enter purchase link" />
          </Form.Item>

          {["tagmango"].includes(formValues?.platformSetup) ? null : (
            <Form.Item
              name="mango"
              label="Mango ID"
              rules={[
                {
                  required: ![
                    "65dc41e3d785b760b33962dd", // tagmango freedom (testing)
                    "6511822e2ca936d3ac3aeafe", // tagmango freedom (prod)
                    "660273b8febbc4230cbec8d0", // tagmango enterprise (testing)
                    "64be75a8bcae92064e321ced", // tagmango enterprise (prod)
                  ].includes(formValues.mango),
                  message: "Please enter mango id",
                },
              ]}
            >
              <Input
                placeholder="Enter the mango id"
                disabled={[
                  "65dc41e3d785b760b33962dd", // tagmango freedom (testing)
                  "6511822e2ca936d3ac3aeafe", // tagmango freedom (prod)
                  "660273b8febbc4230cbec8d0", // tagmango enterprise (testing)
                  "64be75a8bcae92064e321ced", // tagmango enterprise (prod)
                ].includes(formValues.mango)}
              />
            </Form.Item>
          )}

          <Form.Item
            name="revenueShare"
            label="Revenue share"
            rules={[
              {
                required: {
                  value: true,
                  message: "Please enter revenue share",
                },
                type: "number",
                min: 0,
                max: 100,
              },
            ]}
            normalize={(value) => (value ? Number(value) : undefined)}
          >
            <Input placeholder="Enter revenue share" type="number" />
          </Form.Item>

          <Form.Item
            name="status"
            label="Change listing status"
            rules={[
              {
                required: !(
                  formValues.isReferEarnSuspendedProduct ||
                  ["deprecated", "suspended"].includes(formValues.status) ||
                  [
                    "65dc41e3d785b760b33962dd", // tagmango freedom (testing)
                    "6511822e2ca936d3ac3aeafe", // tagmango freedom (prod)
                    "660273b8febbc4230cbec8d0", // tagmango enterprise (testing)
                    "64be75a8bcae92064e321ced", // tagmango enterprise (prod)
                  ].includes(formValues.mango)
                ),
                message: "Please select status",
              },
            ]}
          >
            <Select
              disabled={
                formValues.isReferEarnSuspendedProduct ||
                ["deprecated", "suspended"].includes(formValues.status) ||
                [
                  "65dc41e3d785b760b33962dd", // tagmango freedom (testing)
                  "6511822e2ca936d3ac3aeafe", // tagmango freedom (prod)
                  "660273b8febbc4230cbec8d0", // tagmango enterprise (testing)
                  "64be75a8bcae92064e321ced", // tagmango enterprise (prod)
                ].includes(formValues.mango)
              }
            >
              <Select.Option value="listed">Listed</Select.Option>
              <Select.Option value="hidden">Hidden</Select.Option>
              <Select.Option value="expired">Expired</Select.Option>
            </Select>
          </Form.Item>

          {["listed", "hidden"].includes(formValues?.status) ? (
            <Form.Item label="Enable validity">
              <Switch checked={enableValidity} onChange={setEnableValidity} />
            </Form.Item>
          ) : null}

          {enableValidity && (
            <Form.Item
              name="expireAt"
              label="Valid till"
              rules={[{ required: true, message: "Please select expiry date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                disabledDate={(current) =>
                  current && current < moment().add(1, "d").startOf("day")
                }
                allowClear
                format="DD MMM YYYY"
                onChange={(date) =>
                  form.setFieldsValue({
                    expireAt: date.endOf("day"),
                  })
                }
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <RefernEarnAddUsersModal
        visible={isAddUsersModalVisible}
        onClose={() => setIsAddUsersModalVisible(false)}
        onSave={handleAddUsers}
      />
    </>
  );
};
RefernEarnAddProductMultipleUsersModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default RefernEarnAddProductMultipleUsersModal;
